'use client'

import { ChatCircleIcon } from '@phosphor-icons/react'
import { useMutation } from '@tanstack/react-query'
import { ChevronDown, RotateCcw, X } from 'lucide-react'

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { Student, StudentAction } from '../types'
import { useSharedTRPC } from '../../../context'
import { useStudentMutations } from '../use-student-mutations'

interface ParentCellProps {
  student: Student
  /** False for a viewer who may not act on this row; the control stays visible but inert. */
  canEdit: boolean
  onAction: (action: StudentAction) => void
}

/**
 * The "Orang tua" column: whether this student is linked to a parent's family account, and the one
 * thing the school can do about it right now.
 *
 * Three states, and they are mutually exclusive by construction — the server resolves connectedness
 * from a family membership first, and only reports a pending request for a child that has none.
 */
export function ParentCell({ student, canEdit, onAction }: ParentCellProps) {
  const connection = student.parentConnection

  if (connection.status === 'connected') {
    return (
      <ContactParentButton
        parentEmail={connection.parentEmail}
        canEdit={canEdit}
        onContact={() => onAction({ kind: 'contact-parent', student })}
      />
    )
  }

  if (connection.status === 'pending') {
    return <PendingMenu student={student} requestId={connection.requestId} canEdit={canEdit} />
  }

  return (
    <Button
      variant='ghost'
      size='sm'
      className='srd:text-nowrap'
      disabled={!canEdit}
      onClick={() => onAction({ kind: 'request-home-connect', student })}
    >
      Sambungkan
    </Button>
  )
}

function ContactParentButton({
  parentEmail,
  canEdit,
  onContact,
}: {
  parentEmail: string | null
  canEdit: boolean
  onContact: () => void
}) {
  // A family organization with no resolvable owner is a data anomaly, not a state the school can
  // act on — show that it is connected and stop there. The mutation refuses this case too, so a
  // button here would only ever produce an error toast.
  if (!parentEmail) {
    return (
      <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100 srd:text-nowrap')}>
        Sudah tersambung
      </span>
    )
  }

  // Opens the message dialog rather than a `mailto:`. The parent holds an account by definition
  // here, so the message reaches them in /pesan as well as by email — and the school gets the four
  // templates instead of an empty mail client.
  return (
    <Button size='sm' className='srd:text-nowrap' disabled={!canEdit} onClick={onContact}>
      <ChatCircleIcon className='srd:mr-2 srd:h-4 srd:w-4' />
      Hubungi orang tua
    </Button>
  )
}

function PendingMenu({ student, requestId, canEdit }: { student: Student; requestId: string; canEdit: boolean }) {
  const trpc = useSharedTRPC()
  const { invalidate } = useStudentMutations()

  const resend = useMutation(trpc.homeConnect.resend.mutationOptions())
  const cancel = useMutation(trpc.homeConnect.cancel.mutationOptions())
  const isPending = resend.isPending || cancel.isPending

  const handleResend = async () => {
    try {
      await resend.mutateAsync({ requestId })
      await invalidate()
      toast.success('Permohonan berhasil dikirim ulang.')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Gagal mengirim ulang permohonan')
    }
  }

  const handleCancel = async () => {
    try {
      await cancel.mutateAsync({ requestId })
      await invalidate()
      toast.success(`Permohonan untuk ${student.name} dibatalkan.`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Gagal membatalkan permohonan')
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant='ghost'
            size='sm'
            className='srd:text-nowrap srd:text-navy-100'
            disabled={!canEdit}
            loading={isPending}
          >
            Tunggu konfirmasi
            <ChevronDown className='srd:ml-2 srd:h-4 srd:w-4 srd:text-navy-100' />
          </Button>
        }
      />

      <DropdownMenuContent align='start' className='srd:w-fit srd:max-w-[250px]'>
        <DropdownMenuItem disabled={isPending} onClick={() => void handleResend()}>
          <RotateCcw className='srd:mr-2 srd:h-4 srd:w-4' />
          Kirim ulang permohonan
        </DropdownMenuItem>

        <DropdownMenuItem variant='destructive' disabled={isPending} onClick={() => void handleCancel()}>
          <X className='srd:mr-2 srd:h-4 srd:w-4' />
          Batalkan permohonan
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
